<template>
  <v-app class="root">

    <!-- ── App Bar ── -->
    <v-app-bar
      app
      fixed
      color="#000000"
      elevation="2"
      class="app-bar"
    >
      <a href="/" class="app-bar__logo-link" aria-label="ZaperMenu — página inicial" @click="onHomeClick">
        <img
          :src="require('@/assets/images/zapermenu.png')"
          class="app-bar__logo"
          alt="ZaperMenu"
          width="90"
          height="auto"
        />
      </a>

      <nav class="nav-links d-none d-md-flex ml-8">
        <a
          v-for="item in navItems"
          :key="item.id"
          class="nav-link"
          :class="{ 'nav-link--active': isActive(item) }"
          :href="navHref(item)"
          @click="onNavClick($event, item)"
        >
          {{ item.label }}
        </a>
      </nav>

      <v-spacer />

      <!-- Theme toggle -->
      <v-btn
        icon
        class="white--text mr-1"
        :aria-label="$vuetify.theme.dark ? 'Ativar tema claro' : 'Ativar tema escuro'"
        @click="toggleTheme"
      >
        <v-icon>{{ $vuetify.theme.dark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>

      <!-- "Já sou Cliente" — desktop -->
      <v-btn
        text
        class="white--text d-none d-md-flex ml-1"
        style="border-radius: 10px;"
        @click="openAdmin"
      >
        Já sou Cliente
      </v-btn>

      <!-- Hamburger — mobile -->
      <v-btn
        icon
        class="white--text d-flex d-md-none"
        aria-label="Abrir menu"
        @click="drawer = true"
      >
        <v-icon>mdi-menu</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- ── Mobile Drawer ── -->
    <v-navigation-drawer
      v-model="drawer"
      app
      right
      temporary
      width="280"
      :color="$vuetify.theme.dark ? '#111111' : '#ffffff'"
    >
      <!-- Drawer header -->
      <div class="drawer-header">
        <img
          :src="require('@/assets/images/zapermenu.png')"
          alt="ZaperMenu"
          width="80"
        />
        <v-btn
          icon
          :class="$vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-3'"
          @click="drawer = false"
          aria-label="Fechar menu"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider :dark="$vuetify.theme.dark" class="mx-4" />

      <!-- Drawer nav links -->
      <nav class="drawer-nav">
        <a
          v-for="item in navItems"
          :key="item.id"
          class="drawer-link"
          :class="{ 'drawer-link--active': isActive(item) }"
          :href="navHref(item)"
          @click="onNavClick($event, item); drawer = false"
        >
          {{ item.label }}
        </a>
      </nav>

      <v-divider :dark="$vuetify.theme.dark" class="mx-4 mt-4" />

      <!-- Drawer CTA -->
      <div class="px-4 mt-4">
        <v-btn
          block
          outlined
          :color="$vuetify.theme.dark ? 'white' : '#333333'"
          style="border-radius: 10px;"
          @click="openAdmin"
        >
          Já sou Cliente
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- ── Page content ── -->
    <slot name="principal" />

  </v-app>
</template>

<script>
import { trackClick } from '@/utils/lpTracker';

export default {
  name: 'BaseContainer',

  data: () => ({
    drawer: false,
    activeSection: null,
  }),

  computed: {
    navItems() {
      const base = [
        { label: 'Como Funciona', id: 'como-funciona' },
        { label: 'Recursos',      id: 'recursos'       },
        { label: 'Depoimentos',   id: 'depoimentos'    },
        { label: 'FAQ',           id: 'faq'            },
      ];
      if (this.$store.state.showPlanos) {
        base.splice(2, 0, { label: 'Planos', id: 'planos' });
      }
      // Item com `path` é rota própria, não âncora de seção da home.
      base.push({ label: 'Revenda', id: 'revenda', path: '/revenda' });
      return base;
    },
  },

  mounted() {
    // Lê preferência de tema
    const saved = localStorage.getItem('theme');
    if (saved !== null) {
      this.$vuetify.theme.dark = saved === 'dark';
    }

    // Scroll listener para destacar seção ativa
    this.$nextTick(() => {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll(); // checar posição inicial
    });
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll);
  },

  methods: {
    _onScroll() {
      const navbarHeight = 80;
      const scrollY = window.scrollY + navbarHeight + 16;

      // Percorre as seções de baixo pra cima e pega a primeira cujo topo
      // já passou pelo ponto de detecção — essa é a seção "atual"
      let current = null;
      for (const item of this.navItems) {
        if (item.path) continue;
        const el = document.getElementById(item.id);
        if (!el) continue;
        if (el.offsetTop <= scrollY) {
          current = item.id;
        }
      }
      this.activeSection = current;
    },

    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem('theme', this.$vuetify.theme.dark ? 'dark' : 'light');
    },

    navHref(item) {
      return item.path || `/#${item.id}`;
    },

    isActive(item) {
      return item.path ? this.$route.path === item.path : this.activeSection === item.id;
    },

    onNavClick(event, item) {
      if (item.path) trackClick(`Nav - ${item.label}`);
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      if (item.path) {
        event.preventDefault();
        if (this.$route.path === item.path) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        this.$router.push({ path: item.path });
        return;
      }

      if (!document.getElementById(item.id)) return;
      event.preventDefault();
      this.scrollTo(item.id);
    },

    onHomeClick(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (this.$route.path === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.$router.push({ path: '/' });
    },

    scrollTo(id) {
      this.activeSection = id;
      const offset = 70;

      const doScroll = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      };

      doScroll();

      // Seções lazy acima carregam durante o scroll e deslocam o layout.
      // Re-scroll após elas renderizarem, a menos que o usuário já tenha scrollado.
      let userScrolled = false;
      const markUserScroll = () => { userScrolled = true; };
      window.addEventListener('wheel', markUserScroll, { once: true, passive: true });
      window.addEventListener('touchmove', markUserScroll, { once: true, passive: true });

      setTimeout(() => {
        window.removeEventListener('wheel', markUserScroll);
        window.removeEventListener('touchmove', markUserScroll);
        if (!userScrolled) doScroll();
      }, 900);
    },

    openAdmin() {
      trackClick('Nav - Já sou Cliente');
      window.open('https://admin.zapermenu.com.br/', '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
  .root {
    background: var(--page-bg);
    width: 100vw;
  }

  .app-bar {
    z-index: 999;
  }

  .app-bar__logo-link {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  .app-bar__logo {
    margin-left: 4%;
    cursor: pointer;
  }

  /* ── Desktop nav ── */
  .nav-links {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .nav-link {
    position: relative;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
    background: transparent;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s, background 0.2s;

    &::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%) scaleX(0);
      width: 60%;
      height: 2px;
      background: #f40612;
      border-radius: 1px;
      transition: transform 0.25s ease;
    }

    &:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
    }

    &--active {
      color: #ffffff;
      &::after {
        transform: translateX(-50%) scaleX(1);
      }
    }
  }

  /* ── Mobile drawer ── */
  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 16px 12px;
  }

  .drawer-nav {
    display: flex;
    flex-direction: column;
    padding: 12px 8px;
  }

  .drawer-link {
    display: block;
    width: 100%;
    padding: 14px 20px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
    background: transparent;
    border: none;
    cursor: pointer;
    text-decoration: none;
    text-align: left;
    transition: color 0.2s, background 0.2s;

    &:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
    }

    &--active {
      color: #ffffff;
      background: rgba(244, 6, 18, 0.12);
      border-left: 3px solid #f40612;
      padding-left: 17px;
    }
  }

  /* ── Drawer — light theme ── */
  .root.theme--light {
    .drawer-link {
      color: rgba(0, 0, 0, 0.65);

      &:hover {
        color: #111111;
        background: rgba(0, 0, 0, 0.05);
      }

      &--active {
        color: #e53935;
        background: rgba(244, 6, 18, 0.07);
        border-left-color: #f40612;
      }
    }
  }
</style>
